import { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "../../config/supabaseClient";
import { getComponentsTableName } from "../componentsTable";

export type ArchiveTableName =
  | "stayuptodate_components"
  | "embed_components"
  | "page_components"
  | "template_components";

export function getArchiveTableName(from?: string): ArchiveTableName {
  return getComponentsTableName(from) as ArchiveTableName;
}

export function isMissingArchiveColumnError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const message = String((error as { message?: string }).message ?? "").toLowerCase();
  return (
    message.includes("archived_at") ||
    message.includes("archive_index") ||
    (message.includes("column") && message.includes("does not exist"))
  );
}

export async function getNextArchiveIndex(
  user_id: string,
  from?: string,
  client: SupabaseClient = supabase
): Promise<number> {
  const table = getArchiveTableName(from);
  const { data, error } = await client
    .from(table)
    .select("archive_index")
    .eq("user_id", user_id)
    .eq("status_expiry", "archive")
    .neq("status", "deleted")
    .not("archive_index", "is", null)
    .order("archive_index", { ascending: false })
    .limit(1);

  if (error) {
    if (isMissingArchiveColumnError(error)) return 0;
    throw error;
  }

  const max = data?.[0]?.archive_index;
  const maxNum =
    max === null || max === undefined ? -1 : Number(max);
  return Number.isFinite(maxNum) ? maxNum + 1 : 0;
}

export async function buildArchiveStatusPatch(
  value: string,
  user_id: string,
  from?: string,
  client: SupabaseClient = supabase
): Promise<Record<string, unknown>> {
  const normalized = String(value ?? "").trim().toLowerCase();

  if (normalized === "archive") {
    const nextArchiveIndex = await getNextArchiveIndex(user_id, from, client);
    return {
      archived_at: new Date().toISOString(),
      archive_index: nextArchiveIndex,
    };
  }

  if (normalized === "active" || normalized === "draft") {
    return {
      archived_at: null,
      archive_index: null,
    };
  }

  return {};
}

export async function applyArchiveMetadataForStatus(
  value: string,
  user_id: string,
  component: { id: string | number },
  from?: string,
  client: SupabaseClient = supabase
): Promise<void> {
  const table = getArchiveTableName(from);
  const patch = await buildArchiveStatusPatch(value, user_id, from, client);
  if (Object.keys(patch).length === 0) return;

  const { error } = await client
    .from(table)
    .update(patch)
    .eq("id", component.id)
    .eq("user_id", user_id);

  if (error && !isMissingArchiveColumnError(error)) {
    throw error;
  }
}

export function getArchiveFieldsForInsert(
  statusExpiry: string | undefined,
  archiveIndex: number
): Record<string, unknown> {
  if (String(statusExpiry ?? "").trim().toLowerCase() !== "archive") {
    return {};
  }
  return {
    archived_at: new Date().toISOString(),
    archive_index: archiveIndex,
  };
}
